import { FC } from 'react';
import {
    InternalKPICard,
    InternalDashboardSkeleton,
    InternalDashboardError
} from './';
import {
    RefreshCw,
    AlertCircle,
    Zap
} from 'lucide-react';
import { ServiceStatusInfo } from '../../types/internal';
import { formatNumber } from '../../utils/formatters';
import { SERVICE_TIERS } from '../../constants/infrastructure';
import { InternalServiceRow } from './InternalServiceRow';

interface InternalInfrastructureSectionProps {
    services: ServiceStatusInfo[];
    loading: boolean;
    refresh: () => void;
    error: string | null;
}

export const InternalInfrastructureSection: FC<InternalInfrastructureSectionProps> = ({
    services,
    loading,
    refresh,
    error
}) => {
    if (loading && !services.length) return <InternalDashboardSkeleton />;
    if (error && !services.length) return <InternalDashboardError error={error} onRetry={refresh} />;

    const activeServices = services.filter(s => s.isActive);

    // Consolidate into two simple groups: Infrastructure and Services
    const infraNodes = services.filter(s => SERVICE_TIERS.CORE.some(tier => s.serviceName.startsWith(tier)));
    const serviceNodes = services.filter(s => !SERVICE_TIERS.CORE.some(tier => s.serviceName.startsWith(tier)));

    // Extract renderer breakdown for cleaner JSX
    const rendererNode = serviceNodes.find(s => s.serviceName.includes('renderer') || s.serviceName.includes('mxm'));
    const rendererBreakdown = (rendererNode?.categoryBreakdown || {}) as Record<string, number>;
    const hasRendererBreakdown = Object.keys(rendererBreakdown).length > 0;

    // Compute reliability percentage directly from the active nodes
    const reliabilityPercentage = services.length > 0
        ? ((activeServices.length / services.length) * 100).toFixed(1)
        : '100';

    return (
        <div className="space-y-10 animate-in fade-in duration-700">

            {/* KPI TOP BAR - Dense & Clean */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <InternalKPICard
                    label="Connected Nodes"
                    value={`${activeServices.length} / ${services.length}`}
                    sublabelText="Network Connectivity"
                    primary
                />
                <InternalKPICard
                    label="Global Templates"
                    value={formatNumber(services.reduce((acc, s) => acc + (s.templateCount || 0), 0))}
                    sublabelText="Renderer Capacity"
                />
                <InternalKPICard
                    label="System Reliability"
                    value={`${reliabilityPercentage}%`}
                    sublabelText="Current Fleet Health"
                />
            </div>

            {/* MONITORING SECTIONS */}
            <div className="space-y-12">

                {/* GROUP 1: SYSTEM INFRASTRUCTURE */}
                {infraNodes.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 px-1">
                            <div className="h-0.5 w-8 bg-emerald-500/20 rounded-full" />
                            <h2 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-[0.2em]">System Infrastructure</h2>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                            {infraNodes.map(s => <InternalServiceRow key={s.serviceName} s={s} />)}
                        </div>
                    </div>
                )}

                {/* GROUP 2: SYSTEM SERVICES */}
                {serviceNodes.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 px-1">
                            <div className="h-0.5 w-8 bg-indigo-500/20 rounded-full" />
                            <h2 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-[0.2em]">System Services</h2>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                            {serviceNodes.map(s => <InternalServiceRow key={s.serviceName} s={s} />)}

                            {/* Specialized Breakdown for Renderer */}
                            {hasRendererBreakdown && (
                                <div className="bg-slate-50/50 border-t border-slate-100 p-8">
                                    <div className="flex items-center gap-2.5 mb-6">
                                        <Zap className="w-3.5 h-3.5 text-slate-400" />
                                        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Renderer Category Allocation</h3>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
                                        {Object.entries(rendererBreakdown).map(([cat, count]) => (
                                            <div key={cat} className="space-y-1.5">
                                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight block truncate" title={cat}>
                                                    {cat}
                                                </span>
                                                <div className="flex items-baseline gap-1.5">
                                                    <span className="text-xl font-extrabold text-slate-900 leading-none">{count}</span>
                                                    <span className="text-[9px] font-bold text-slate-300 uppercase">TEMPLATES</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* REFRESH CONTROL */}
            <div className="flex items-center justify-between pt-4">
                <div className="flex items-center gap-4 text-slate-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs font-medium italic">Service telemetry is updated automatically. Alerts will trigger on node disruption.</span>
                </div>
                <button
                    onClick={refresh}
                    className="flex items-center gap-2.5 h-10 px-6 bg-slate-900 text-white text-[11px] font-bold rounded-lg hover:bg-slate-800 transition-all shadow-md active:scale-95 uppercase tracking-widest disabled:opacity-50"
                    disabled={loading}
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh Node Status
                </button>
            </div>

        </div>
    );
};
